import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Upload, RefreshCw, CheckCircle2, Loader2, X, ScanLine, UserSquare } from 'lucide-react';
import { uploadOnboardingDoc } from '../services/api';

// Ultra-pro document/selfie capture: live camera + file-upload fallback, instant upload.
export const DocumentCapture = ({ doc, subject, country, applicationId, value, onUploaded }) => {
  const isSelfie = doc.capture === 'selfie';
  const isCameraDoc = doc.capture === 'camera' || isSelfie;

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const [cameraOn, setCameraOn] = useState(false);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | uploading | done | error
  const [error, setError] = useState('');

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const startCamera = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: isSelfie ? 'user' : 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOn(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (e) {
      setError('No pudimos acceder a la cámara. Sube una foto desde tu dispositivo.');
      setCameraOn(false);
    }
  };

  const doUpload = async (blob, filename) => {
    setStatus('uploading');
    setError('');
    const fd = new FormData();
    fd.append('file', blob, filename);
    fd.append('document_type', doc.key);
    fd.append('subject', subject);
    fd.append('country', country);
    if (applicationId) fd.append('application_id', applicationId);
    try {
      const res = await uploadOnboardingDoc(fd);
      setStatus('done');
      onUploaded && onUploaded(subject, doc.key, res.data);
    } catch (e) {
      setStatus('error');
      setError(e.response?.data?.detail || 'Error al subir el documento');
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 720;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setPreview(dataUrl);
    stopCamera();
    canvas.toBlob((blob) => {
      if (blob) doUpload(blob, `${doc.key}.jpg`);
    }, 'image/jpeg', 0.9);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    doUpload(file, file.name || `${doc.key}.jpg`);
  };

  const reset = () => {
    setPreview(null);
    setStatus('idle');
    setError('');
    stopCamera();
  };

  const uploaded = status === 'done' || !!value;

  return (
    <div
      className={`rounded-2xl border ${uploaded ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white'} p-4 transition-colors`}
      data-testid={`doc-capture-${subject}-${doc.key}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${uploaded ? 'bg-emerald-500' : 'bg-[#0A2463]'}`}>
            {isSelfie ? <UserSquare className="w-5 h-5 text-white" /> : <ScanLine className="w-5 h-5 text-white" />}
          </div>
          <div>
            <p className="font-semibold text-slate-900 text-sm leading-tight">{doc.label}</p>
            <p className="text-xs text-slate-400">{doc.required ? 'Obligatorio' : 'Opcional'}</p>
          </div>
        </div>
        {uploaded && <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
      </div>

      {/* Camera live view */}
      {cameraOn && (
        <div className="relative rounded-xl overflow-hidden bg-black mb-3">
          <video ref={videoRef} playsInline muted className="w-full h-48 object-cover" />
          <div className="absolute inset-0 pointer-events-none border-2 border-white/60 m-4 rounded-lg" />
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
            <button
              onClick={capturePhoto}
              className="bg-[#E91E8C] hover:bg-[#c21473] text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg"
              data-testid={`capture-shoot-${subject}-${doc.key}`}
            >
              <Camera className="w-4 h-4 inline mr-1" /> Capturar
            </button>
            <button onClick={stopCamera} className="bg-white/90 text-slate-700 text-sm px-3 py-2 rounded-full">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && !cameraOn && (
        <div className="relative rounded-xl overflow-hidden mb-3 border border-slate-200">
          <img src={preview} alt={doc.label} className="w-full h-40 object-cover" />
          {status === 'uploading' && (
            <div className="absolute inset-0 bg-slate-900/50 flex flex-col items-center justify-center text-white text-xs gap-1">
              <Loader2 className="w-6 h-6 animate-spin" />
              Cifrando y almacenando...
            </div>
          )}
          {status === 'done' && (
            <div className="absolute top-2 right-2 bg-emerald-500 text-white text-[11px] px-2 py-1 rounded-full flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Verificado
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {!cameraOn && (
        <div className="flex gap-2">
          {!uploaded && isCameraDoc && (
            <button
              onClick={startCamera}
              className="flex-1 flex items-center justify-center gap-2 bg-[#0A2463] hover:bg-[#08204f] text-white text-sm font-medium py-2.5 rounded-xl transition-colors"
              data-testid={`capture-open-camera-${subject}-${doc.key}`}
            >
              <Camera className="w-4 h-4" /> Tomar foto
            </button>
          )}
          {!uploaded && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 flex items-center justify-center gap-2 border border-slate-300 hover:border-[#0A2463] text-slate-700 text-sm font-medium py-2.5 rounded-xl transition-colors"
              data-testid={`capture-upload-${subject}-${doc.key}`}
            >
              <Upload className="w-4 h-4" /> Subir archivo
            </button>
          )}
          {uploaded && (
            <button
              onClick={reset}
              className="flex-1 flex items-center justify-center gap-2 text-slate-500 hover:text-[#0A2463] text-sm py-2.5 rounded-xl border border-slate-200"
              data-testid={`capture-retake-${subject}-${doc.key}`}
            >
              <RefreshCw className="w-4 h-4" /> Volver a capturar
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            capture={isSelfie ? 'user' : 'environment'}
            onChange={handleFile}
            className="hidden"
          />
        </div>
      )}

      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default DocumentCapture;
